import React from "react";
import { fmtNumber } from "../../shared/format";
import { intelOf, labelFor } from "./conversationIntel";

// Chat row content in the worklist: outcome badge, stay-type chip, the count of
// messages the visitor sent, and — when a person resolved — a deliberate lift.
//
// NOT the voice row. There is no duration worth showing (a chat window can sit
// open for half an hour with two messages in it), no recording to play and no
// greeting to abandon, so none of those three is rendered. They are absent, not
// empty.
//
// ANONYMITY IS THE DEFAULT, NOT A DEFECT. About six in every hundred chat
// visitors identify themselves; the rest are simply people who did not, which is
// what web chat is. So an anonymous row gets no "unknown", no muted text, no
// empty-state affordance — it renders exactly like any other row with one fewer
// chip. The lift goes on the RARE case instead, because a resolved person is the
// valuable one.
//
// Render-only. Every value comes from the row; nothing here derives or infers.

const OUTCOME_TONE = {
  reservation_made: "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200 border-emerald-200 dark:border-emerald-800",
  reservation_assisted: "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200 border-emerald-200 dark:border-emerald-800",
  abandoned: "bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200 border-red-200 dark:border-red-800",
};
const NEUTRAL_TONE =
  "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700";
const PERSON_TONE =
  "bg-[#EAF6FF] dark:bg-blue-900/40 text-[#1B5E86] dark:text-[#9BDBFF] border-[#C7E7FF] dark:border-blue-800 font-semibold";

const Chip = ({ children, className = "", title, testId }) => (
  <span
    title={title}
    data-testid={testId}
    className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border shrink-0 ${className}`}
  >
    {children}
  </span>
);

const PersonIcon = () => (
  <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

/**
 * The QUESTION count the spec actually asked for, with the old substitution as
 * a fallback.
 *
 * `question_count` was added 2026-08-27 and counts questions the guest actually
 * asked, from key_questions_asked. Until then no deployed function returned one,
 * and this column rendered `human_message_count` labelled "N from visitor" —
 * deliberately not "questions", because most visitor messages are answers to the
 * agent's questions rather than questions themselves.
 *
 * Both are read, never derived. The label follows whichever is in play, so the
 * column never claims to be counting questions while showing messages.
 *
 * They live on DIFFERENT objects and neither is on both: `question_count` comes
 * from the fn_get_conversation_list row, which mergeIntel puts on `row.intel`,
 * while `human_message_count` is a column on the transcripts record at
 * `row.convo`. Reading the question count off `row.convo` finds nothing and
 * renders the fallback forever, with no error to notice.
 */
const visitorCounts = (row) => {
  const q = intelOf(row).question_count;
  if (q != null) return { n: Number(q), unit: 'question', asked: true };
  const m = row?.convo?.human_message_count;
  if (m != null) return { n: Number(m), unit: 'message', asked: false };
  return null;
};

const ChatRowIntel = ({ row }) => {
  const it = intelOf(row);
  const personId = row?.convo?.person_id || it.person_id || null;
  const counted = visitorCounts(row);
  const hasAny = it.outcome || it.stay_type_sought || counted != null || personId;
  if (!hasAny) return null;

  // `unknown` is a stored classification, not an absence, but it tells a reader
  // nothing in a dense list — so it is dropped from the row and kept in the
  // filters, where isolating those rows is the point.
  const stayType = it.stay_type_sought && it.stay_type_sought !== "unknown" ? it.stay_type_sought : null;

  return (
    <div className="flex items-center flex-wrap gap-1 mt-1" data-testid="chat-row-intel">
      {personId && (
        <Chip className={PERSON_TONE} title="This visitor is a known guest" testId="chat-row-person">
          <PersonIcon />
          Known guest
        </Chip>
      )}
      {it.outcome && (
        <Chip className={OUTCOME_TONE[it.outcome] || NEUTRAL_TONE} title={`Outcome: ${labelFor(it.outcome)}`}>
          {labelFor(it.outcome)}
        </Chip>
      )}
      {stayType && <Chip className={NEUTRAL_TONE} title={`Stay type: ${labelFor(stayType)}`}>{labelFor(stayType)}</Chip>}
      {counted != null && (
        <span
          className="text-[10px] text-gray-500 dark:text-gray-400 shrink-0"
          title={counted.asked ? "Questions the guest asked" : "Messages sent by the visitor"}
          data-testid="chat-row-messages"
        >
          {/* The label follows the field. "3 questions" and "3 from visitor" are
              different claims, and only one of them is true of a given payload. */}
          {counted.asked
            ? `${fmtNumber(counted.n)} ${counted.n === 1 ? 'question' : 'questions'}`
            : `${fmtNumber(counted.n)} from visitor`}
        </span>
      )}
    </div>
  );
};

export default ChatRowIntel;
